
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import BackofficeLayout from "@/components/layouts/BackofficeLayout";
import { Button } from "@/components/ui/button";
import { Loader2, ChevronLeft, Save } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { documentApi } from "@/services/api";
import { useToast } from "@/components/ui/use-toast";
import DocumentForm from "@/components/documents/DocumentForm";


const DocumentEdit = () => {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  console.log(`[${new Date().toISOString()}] DocumentEdit: Rendering with id: ${id}`);
  
  const documentId = id ? parseInt(id) : undefined;
  
  const { 
    data: documentData, 
    isLoading, 
    isError, 
    error,
    refetch
  } = useQuery({
    queryKey: ['document', documentId],
    queryFn: async () => {
      if (!documentId) {
        throw new Error('No document ID provided');
      }
      const doc = await documentApi.getById(documentId);
      
      // Sort form steps by step_order if they exist
      if (doc.document_form?.form_steps) {
        doc.document_form.form_steps.sort((a, b) => a.step_order - b.step_order);
        console.log(`[${new Date().toISOString()}] DocumentEdit: Sorted form steps:`, 
          doc.document_form.form_steps.map(step => `Step ${step.id}: order ${step.step_order}`));
      }
      
      return doc;
    },
    enabled: !!documentId,
    meta: {
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title: "Error loading document",
          description: error.message || "Failed to fetch document details",
        });
      }
    }
  });

  const handleUpdateSuccess = async () => {
    console.log(`[${new Date().toISOString()}] DocumentEdit: Document updated successfully`);
    
    // Refetch the document data to get the latest status
    await refetch();
    
    toast({
      title: "Document updated",
      description: "The document has been updated successfully.",
    });
  };

  if (isLoading) {
    return (
      <BackofficeLayout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
        </div>
      </BackofficeLayout>
    );
  }

  if (isError || !documentData) {
    return (
      <BackofficeLayout>
        <div className="space-y-6">
          <Alert variant="destructive">
            <AlertDescription>
              {(error as any)?.message || "Failed to load document. The document may have been deleted or you don't have permission to view it."}
            </AlertDescription>
          </Alert>
        </div>
      </BackofficeLayout>
    );
  }

  console.log(`[${new Date().toISOString()}] DocumentEdit: Loaded document data:`, documentData.name);

  return (
    <BackofficeLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="space-y-1">
            <Button asChild variant="outline" size="sm">
              <Link to="/backoffice/documents">
                <ChevronLeft className="mr-1 h-4 w-4" />
                Back to Documents
              </Link>
            </Button>
            <h1 className="text-3xl font-bold tracking-tight">
              Edit Document: {documentData.name}
            </h1>
            <p className="text-gray-500">
              Update document information, groups, and content
            </p>
          </div>
          
          <Button 
            type="submit"
            form="document-edit-form"
          >
            <Save className="h-4 w-4 mr-2" />
            Save Changes
          </Button>
        </div>

        <DocumentForm 
          documentProp={documentData} 
          onSuccess={handleUpdateSuccess} 
          submitButtonText="Save Changes" 
          formId="document-edit-form"
          dialogFormSubmitDisabled={false}
          preventFormSubmission={false}
        />
      </div>
    </BackofficeLayout>
  );
};

export default DocumentEdit;
